/**
 * MovingOnboardingWizard — 3-step first-run wizard for Moving-industry admins.
 *
 * Trigger: user.role in {admin,dispatcher} AND industry_type='moving'
 * (or company_category='moving_company') AND user.preferences.moving_onboarded_v1
 * is not truthy. Dismissal writes the flag back so the wizard never
 * reappears.
 *
 * Steps: (1) Add first Foreman, (2) Add first Customer, (3) Go create a
 * BOL / closing. Each step supports Skip and Save; the wizard closes
 * automatically after step 3 (or when the user hits "Do this later").
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { HardHat, Users, ClipboardCheck, ChevronRight, CheckCircle2 } from 'lucide-react';

const STEP_KEYS = ['foreman', 'customer', 'bol'];
const STEP_ICONS = [HardHat, Users, ClipboardCheck];
const STEP_TINTS = ['text-amber-400', 'text-sky-400', 'text-emerald-400'];

export const MovingOnboardingWizard = () => {
  const { t } = useTranslation();
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const logEvent = useCallback((event, extra = {}) => {
    const idx = extra.step_index ?? step;
    const payload = { event, step: STEP_KEYS[idx], step_index: idx, meta: extra.meta || null };
    api.post('/moving-onboarding/events', payload).catch(() => { /* silent */ });
  }, [api, step]);
  const bootedRef = useRef(false);

  const [foreman, setForeman] = useState({ name: '', email: '', phone: '', password: '' });
  const [customer, setCustomer] = useState({ name: '', email: '', phone: '' });

  const isMovingAdmin = useMemo(() => {
    if (!user) return false;
    const industry = user.industry_type === 'moving' || user.company_category === 'moving_company';
    const role = user.role;
    return industry && (role === 'admin' || role === 'dispatcher');
  }, [user]);

  const already = user?.preferences?.moving_onboarded_v1 === true;

  // Boot log — one shot when the wizard first appears for a user.
  useEffect(() => {
    if (!bootedRef.current && isMovingAdmin && !already) {
      bootedRef.current = true;
      logEvent('step_viewed', { step_index: 0 });
    }
  }, [isMovingAdmin, already, logEvent]);

  const markDone = async (reason = 'wizard_dismissed') => {
    logEvent(reason);
    try {
      await api.patch('/auth/preferences', { moving_onboarded_v1: true });
    } catch (e) { /* non-fatal */ }
    setOpen(false);
  };

  if (!isMovingAdmin || already) return null;

  const saveForeman = async () => {
    if (!foreman.name.trim() || !foreman.email.trim() || !foreman.password.trim()) {
      toast.error(t('movingOnboarding.errors.requiredForeman', 'Name, email and password are required'));
      return;
    }
    if (foreman.password.length < 6) { toast.error(t('movingOnboarding.errors.shortPassword', 'Password must be 6+ characters')); return; }
    setBusy(true);
    try {
      await api.post('/admin/users', {
        email: foreman.email.trim(),
        password: foreman.password,
        name: foreman.name.trim(),
        role: 'foreman',
        phone: foreman.phone.trim() || null,
      });
      toast.success(t('movingOnboarding.toasts.foremanAdded', { name: foreman.name, defaultValue: 'Foreman {{name}} added' }));
      logEvent('step_completed', { step_index: 0 });
      setStep(1);
      logEvent('step_viewed', { step_index: 1 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('movingOnboarding.errors.foremanFailed', 'Could not add foreman'));
    } finally { setBusy(false); }
  };

  const saveCustomer = async () => {
    if (!customer.name.trim()) { toast.error(t('movingOnboarding.errors.requiredCustomer', 'Customer name is required')); return; }
    setBusy(true);
    try {
      await api.post('/customers', {
        name: customer.name.trim(),
        email: customer.email.trim() || undefined,
        phone: customer.phone.trim() || undefined,
      });
      toast.success(t('movingOnboarding.toasts.customerAdded', { name: customer.name, defaultValue: 'Customer {{name}} created' }));
      logEvent('step_completed', { step_index: 1 });
      setStep(2);
      logEvent('step_viewed', { step_index: 2 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('movingOnboarding.errors.customerFailed', 'Could not create customer'));
    } finally { setBusy(false); }
  };

  const goToClosings = async () => {
    logEvent('step_completed', { step_index: 2 });
    await markDone('wizard_completed');
    navigate('/moving/closings');
  };

  const skipCurrent = () => {
    logEvent('step_skipped', { step_index: step });
    if (step < STEP_KEYS.length - 1) {
      const nextStep = step + 1;
      setStep(nextStep);
      logEvent('step_viewed', { step_index: nextStep });
    } else {
      markDone('wizard_dismissed');
    }
  };

  const skipAll = async () => { await markDone('wizard_dismissed'); };

  const Icon = STEP_ICONS[step];
  const tint = STEP_TINTS[step];
  const currentTitle = t(`movingOnboarding.steps.${STEP_KEYS[step]}.title`);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) markDone(); }}>
      <DialogContent
        data-testid="moving-onboarding-wizard"
        className="bg-slate-950 border border-slate-800 text-white max-w-lg"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`h-11 w-11 rounded-xl bg-slate-900 border border-slate-800 grid place-items-center ${tint}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-white">{t('movingOnboarding.title', 'Welcome to Drayly Moving')}</DialogTitle>
              <DialogDescription className="text-slate-400">{t('movingOnboarding.subtitle', 'Three quick steps to get your team dispatching today.')}</DialogDescription>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            {STEP_KEYS.map((id, i) => (
              <div
                key={id}
                className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-emerald-500' : 'bg-slate-800'}`}
                data-testid={`onboarding-progress-${i}`}
              />
            ))}
          </div>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="text-sm text-slate-400 flex items-center gap-2">
            {t('movingOnboarding.stepLabel', { current: step + 1, total: STEP_KEYS.length, defaultValue: 'Step {{current}} of {{total}}' })} · <span className="text-white font-medium">{currentTitle}</span>
          </div>

          {step === 0 && (
            <div className="space-y-3">
              <div>
                <Label className="text-slate-300 text-xs">{t('movingOnboarding.foreman.name', 'Foreman Name')}</Label>
                <Input data-testid="wiz-foreman-name" value={foreman.name} onChange={(e) => setForeman({ ...foreman, name: e.target.value })} placeholder={t('movingOnboarding.foreman.namePh', 'Miguel Rivera')} className="bg-slate-900 border-slate-800 text-white" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-xs">{t('movingOnboarding.foreman.email', 'Email')}</Label>
                  <Input data-testid="wiz-foreman-email" value={foreman.email} onChange={(e) => setForeman({ ...foreman, email: e.target.value })} placeholder="miguel@yourco.com" className="bg-slate-900 border-slate-800 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">{t('movingOnboarding.foreman.phone', 'Phone')}</Label>
                  <Input data-testid="wiz-foreman-phone" value={foreman.phone} onChange={(e) => setForeman({ ...foreman, phone: e.target.value })} placeholder="(555) 123-4567" className="bg-slate-900 border-slate-800 text-white" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300 text-xs">{t('movingOnboarding.foreman.password', 'Temporary Password (min 6)')}</Label>
                <Input data-testid="wiz-foreman-password" type="text" value={foreman.password} onChange={(e) => setForeman({ ...foreman, password: e.target.value })} placeholder={t('movingOnboarding.foreman.passwordPh', 'Foreman can change on first login')} className="bg-slate-900 border-slate-800 text-white" />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3">
              <div>
                <Label className="text-slate-300 text-xs">{t('movingOnboarding.customer.name', 'Customer / Broker Name')}</Label>
                <Input data-testid="wiz-customer-name" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} placeholder="ACME Van Lines" className="bg-slate-900 border-slate-800 text-white" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-xs">{t('movingOnboarding.customer.email', 'Email')}</Label>
                  <Input data-testid="wiz-customer-email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} placeholder="dispatch@acme.com" className="bg-slate-900 border-slate-800 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">{t('movingOnboarding.customer.phone', 'Phone')}</Label>
                  <Input data-testid="wiz-customer-phone" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} placeholder="(555) 987-6543" className="bg-slate-900 border-slate-800 text-white" />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3 py-2">
              <div className="rounded-lg bg-slate-900 border border-slate-800 p-4 space-y-2">
                <div className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-medium">{t('movingOnboarding.bol.readyHeading', "You're all set.")}</span>
                </div>
                <p className="text-sm text-slate-400">
                  {t('movingOnboarding.bol.readyBody', 'Head to Closings to snap a BOL / packet photo. Our OCR will parse the container fees, mileage, labor and helpers so your foreman only tunes the numbers instead of typing them.')}
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              className="text-slate-400 hover:text-white"
              onClick={skipAll}
              data-testid="wiz-skip-all"
            >
              {t('movingOnboarding.actions.doLater', 'Do this later')}
            </Button>
            {step < STEP_KEYS.length - 1 && (
              <Button
                variant="ghost"
                className="text-slate-500 hover:text-white text-xs"
                onClick={skipCurrent}
                data-testid="wiz-skip-step"
              >
                {t('movingOnboarding.actions.skipStep', 'Skip this step')}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step === 0 && (
              <Button
                onClick={saveForeman}
                disabled={busy}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="wiz-foreman-save"
              >
                {busy ? t('movingOnboarding.actions.saving', 'Saving…') : (<>{t('movingOnboarding.actions.addForeman', 'Add Foreman')} <ChevronRight className="h-4 w-4 ml-1" /></>)}
              </Button>
            )}
            {step === 1 && (
              <Button
                onClick={saveCustomer}
                disabled={busy}
                className="bg-sky-600 hover:bg-sky-700"
                data-testid="wiz-customer-save"
              >
                {busy ? t('movingOnboarding.actions.saving', 'Saving…') : (<>{t('movingOnboarding.actions.addCustomer', 'Add Customer')} <ChevronRight className="h-4 w-4 ml-1" /></>)}
              </Button>
            )}
            {step === 2 && (
              <Button
                onClick={goToClosings}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="wiz-finish"
              >
                {t('movingOnboarding.actions.takeMe', 'Take me to Closings')}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MovingOnboardingWizard;
